// Session-scoped asset registry for the map editor. Imported .glb meshes,
// .hdr environments and .png thumbnails get a stable id and a session blob
// URL; map JSON refers to them via `asset://<id>` so the schema doesn't have
// to know about the difference between disk-backed and session-backed
// resources.
//
// Lifecycle:
//   - Editor opens → registry is populated by import-zip + drag-drop.
//   - Editor exits → disposeAll() revokes every blob URL.
//   - Export ZIP packs the bytes under `assets/<name>` and rewrites every
//     `asset://<id>` reference.
//   - Import ZIP unpacks `assets/*` into the registry and rewrites
//     `assets/<name>` references back to `asset://<id>`.

export type AssetKind = 'glb' | 'hdr' | 'png';

export interface EditorAsset {
  id: string;
  kind: AssetKind;
  name: string;
  bytes: Uint8Array;
  blobUrl: string;
}

const TABLE = new Map<string, EditorAsset>();

const MIME_BY_KIND: Record<AssetKind, string> = {
  glb: 'model/gltf-binary',
  hdr: 'image/vnd.radiance',
  png: 'image/png'
};

const ASSET_PROTOCOL = 'asset://';

let counter = 0;
function nextId(): string {
  counter += 1;
  return `a${Date.now().toString(36)}_${counter.toString(36)}`;
}

function kindFromName(name: string): AssetKind | null {
  const lower = name.toLowerCase();
  if (lower.endsWith('.glb') || lower.endsWith('.gltf')) return 'glb';
  if (lower.endsWith('.hdr')) return 'hdr';
  if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'png';
  return null;
}

export function isAssetUrl(url: string | undefined | null): boolean {
  return typeof url === 'string' && url.startsWith(ASSET_PROTOCOL);
}

export function parseAssetUrl(url: string): string | null {
  return isAssetUrl(url) ? url.slice(ASSET_PROTOCOL.length) : null;
}

/** Translate a schema URL to a load-time URL. `asset://<id>` resolves to the
 *  in-session blob URL; everything else is returned unchanged. Loaders and
 *  the playtest hand-off both call this. */
export function resolveUrl(url: string | undefined | null): string {
  if (!url) return url ?? '';
  const id = parseAssetUrl(url);
  if (!id) return url;
  const asset = TABLE.get(id);
  if (!asset) {
    console.warn(`[EditorAssets] resolveUrl: unknown asset id "${id}"`);
    return url;
  }
  return asset.blobUrl;
}

export function makeAssetUrl(id: string): string {
  return `${ASSET_PROTOCOL}${id}`;
}

export function getAsset(id: string): EditorAsset | undefined {
  return TABLE.get(id);
}

export function listAssets(): EditorAsset[] {
  return [...TABLE.values()];
}

export async function registerAssetFromFile(file: File): Promise<EditorAsset> {
  const kind = kindFromName(file.name);
  if (!kind) {
    throw new Error(`[EditorAssets] unsupported file type: ${file.name}`);
  }
  const bytes = new Uint8Array(await file.arrayBuffer());
  return registerAssetFromBytes(bytes, file.name, kind);
}

export function registerAssetFromBytes(bytes: Uint8Array, name: string, kind: AssetKind): EditorAsset {
  const id = nextId();
  // TS strictness on ArrayBufferLike vs ArrayBuffer in newer DOM lib defs
  // — the cast is safe; Blob accepts any TypedArray.
  const blob = new Blob([bytes as unknown as BlobPart], { type: MIME_BY_KIND[kind] });
  const blobUrl = URL.createObjectURL(blob);
  const asset: EditorAsset = { id, kind, name, bytes, blobUrl };
  TABLE.set(id, asset);
  return asset;
}

export function disposeAsset(id: string): void {
  const asset = TABLE.get(id);
  if (!asset) return;
  URL.revokeObjectURL(asset.blobUrl);
  TABLE.delete(id);
}

export function disposeAll(): void {
  for (const asset of TABLE.values()) URL.revokeObjectURL(asset.blobUrl);
  TABLE.clear();
}
